import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import en from './locales/en';

export const DEFAULT_LOCALE = 'en';
const catalogs = new Map([[DEFAULT_LOCALE, en]]);

/** Add or extend a locale catalog without changing application components. */
export const registerLocale = (locale, messages) => {
  if (!locale || !messages || typeof messages !== 'object') {
    throw new TypeError('registerLocale requires a locale name and message object');
  }
  catalogs.set(locale, { ...(catalogs.get(locale) || {}), ...messages });
};

export const hasLocale = (locale) => catalogs.has(locale);

/** Resolve a message with English fallback and {variable} interpolation. */
export const translate = (key, variables = {}, locale = DEFAULT_LOCALE) => {
  const message = catalogs.get(locale)?.[key] ?? catalogs.get(DEFAULT_LOCALE)?.[key];
  if (message === undefined) return key;

  return String(message).replace(/\{(\w+)\}/g, (match, variable) => (
    Object.prototype.hasOwnProperty.call(variables, variable)
      ? String(variables[variable])
      : match
  ));
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children, initialLocale = DEFAULT_LOCALE }) => {
  const [locale, setRequestedLocale] = useState(
    hasLocale(initialLocale) ? initialLocale : DEFAULT_LOCALE
  );
  const setLocale = useCallback((nextLocale) => {
    if (!hasLocale(nextLocale)) throw new Error(`Locale "${nextLocale}" is not registered`);
    setRequestedLocale(nextLocale);
  }, []);
  const t = useCallback(
    (key, variables) => translate(key, variables, locale),
    [locale]
  );
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside I18nProvider');
  return context;
};

export { en };
